import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, apiUpload, currentUser, downloadDocument } from "../lib/api";
import { formatQAR } from "../lib/money";
import { DocumentsPanel } from "../components/DocumentsPanel";
import { FollowUpsPanel } from "../components/FollowUpsPanel";
import { ApprovalsPanel } from "../components/ApprovalsPanel";
import { AuditHistoryPanel } from "../components/AuditHistoryPanel";
import { FinancialsPanel } from "../components/FinancialsPanel";
import { ChequesPanel } from "../components/ChequesPanel";
import { ExpensePanel } from "../components/ExpensePanel";
const TABS = ["Overview", "Timeline", "Financials", "Documents", "Follow-ups", "Approvals", "Audit History"];
// Legal Stage is a free-text field on the backend (existing cases carry
// legacy values imported from Excel), so this isn't an enum — it's the set
// of stages actually in use today, in roughly the order a case moves
// through them. "Other…" in the dropdown below still allows any value the
// list doesn't cover.
// Mirrors the backend's DIRECTLY_SETTABLE_STATUSES (cases.routes.ts): the
// workflow statuses a case can move through from the edit form. CLOSED,
// WITHDRAWN, DISMISSED, WRITTEN_OFF and RECOVERY_SETTLEMENT are deliberately
// left out — those only happen through the Approvals tab's actions (Write
// Off, Withdraw, Close With Outstanding, Cancel, Settlement), which carry a
// reason and an approval chain.
const DIRECTLY_SETTABLE_STATUSES = [
    "NEW_CASE",
    "DOCUMENTS_PENDING",
    "FILED",
    "COURT_PROCEEDINGS",
    "JUDGMENT",
    "ENFORCEMENT",
    "SETTLEMENT_NEGOTIATION",
    "PARTIALLY_RECOVERED",
    "FULLY_RECOVERED",
    "ON_HOLD",
];
const LEGAL_STAGES = [
    "NEW_CASE",
    "Not specified",
    "Legal Notice Issued",
    "Prosecution - Sent To Court",
    "Preservation- Procedural",
    "Conviction - Judge Executed",
    "Conviction - Judge Under Execution",
    "Judge Executed",
    "Judge Under Execution",
    "Customer Paid at court",
    "Closed",
];
export function CaseDetail() {
    const { id } = useParams();
    const [theCase, setCase] = useState(null);
    const [timeline, setTimeline] = useState([]);
    const [tab, setTab] = useState("Overview");
    const [error, setError] = useState(null);
    const [newComment, setNewComment] = useState("");
    const [addingUpdate, setAddingUpdate] = useState(false);
    const [updateDate, setUpdateDate] = useState(() => new Date().toISOString().slice(0, 10));
    const [updateFile, setUpdateFile] = useState(null);
    const [updateFileKey, setUpdateFileKey] = useState(0);
    const [editingUpdateId, setEditingUpdateId] = useState(null);
    const [editComment, setEditComment] = useState("");
    const [editDate, setEditDate] = useState("");
    const [savingEdit, setSavingEdit] = useState(false);
    const [companies, setCompanies] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [legalFirms, setLegalFirms] = useState([]);
    const [users, setUsers] = useState([]);
    const [coordinators, setCoordinators] = useState([]);
    const [editing, setEditing] = useState(false);
    const [editForm, setEditForm] = useState({});
    const [editResponsibleIds, setEditResponsibleIds] = useState([]);
    const [editSaving, setEditSaving] = useState(false);
    // Whether the Legal Stage select is on "Other…" — tracked separately
    // from editForm.legalStage so picking Other and then clearing the text
    // box doesn't silently snap the dropdown back to "— Select —".
    const [stageOther, setStageOther] = useState(false);
    const user = currentUser();
    function loadCase() {
        if (!id)
            return;
        api
            .get(`/cases/${id}`)
            .then(setCase)
            .catch(() => setError("Couldn't load this case."));
    }
    function loadTimeline() {
        if (!id)
            return;
        api
            .get(`/cases/${id}/timeline`)
            .then((r) => setTimeline(r.items))
            .catch(() => { });
    }
    useEffect(loadCase, [id]);
    useEffect(loadTimeline, [id]);
    useEffect(() => {
        api.get("/companies").then((r) => setCompanies(r.items));
        api.get("/customers").then((r) => setCustomers(r.items));
        api.get("/legal-firms").then((r) => setLegalFirms(r.items));
        api.get("/users").then((r) => setUsers(r.items));
        api.get("/users").then((r) => setCoordinators(r.items));
    }, []);
    function startEditUpdate(e) {
        setEditingUpdateId(e.id);
        setEditComment(e.comment);
        setEditDate(e.occurredAt.slice(0, 10));
    }
    async function saveEditUpdate(e) {
        if (!editComment.trim())
            return;
        setSavingEdit(true);
        try {
            await api.put(`/cases/${id}/updates/${e.id}`, {
                comment: editComment.trim(),
                // Only send the date if it was changed, so the original time is kept otherwise.
                occurredAt: editDate && editDate !== e.occurredAt.slice(0, 10) ? new Date(editDate).toISOString() : undefined,
            });
            setEditingUpdateId(null);
            loadTimeline();
        }
        catch {
            setError("Couldn't save the change.");
        }
        finally {
            setSavingEdit(false);
        }
    }
    async function addUpdate() {
        if (!newComment || !theCase)
            return;
        setAddingUpdate(true);
        try {
            const today = new Date().toISOString().slice(0, 10);
            const created = await api.post(`/cases/${id}/updates`, {
                comment: newComment,
                stageAtUpdate: theCase.legalStage,
                // Only send a date when it was changed, so "today" keeps the exact time.
                occurredAt: updateDate && updateDate !== today ? new Date(updateDate).toISOString() : undefined,
            });
            if (updateFile) {
                const form = new FormData();
                form.append("file", updateFile);
                form.append("documentType", "SUPPORTING_EVIDENCE");
                form.append("caseUpdateId", created.id);
                try {
                    await apiUpload(`/cases/${id}/documents`, form);
                }
                catch {
                    setError("Update added, but the attachment upload failed (PDF, PNG, JPEG, WebP up to 15MB).");
                }
            }
            setNewComment("");
            setUpdateFile(null);
            setUpdateFileKey((k) => k + 1);
            setUpdateDate(today);
            loadTimeline();
        }
        catch {
            setError("Couldn't add the update.");
        }
        finally {
            setAddingUpdate(false);
        }
    }
    function startEdit() {
        if (!theCase)
            return;
        setEditForm({
            caseNumber: theCase.caseNumber ?? "",
            courtNumber: theCase.courtNumber ?? "",
            requestRefNumber: theCase.requestRefNumber ?? "",
            fileNumber: theCase.fileNumber ?? "",
            authorizedSignatory: theCase.authorizedSignatory ?? "",
            legalStage: theCase.legalStage ?? "",
            caseStatus: theCase.caseStatus,
            priority: theCase.priority ?? "MEDIUM",
            legalFirmId: theCase.legalFirmId ?? "",
            chequeAmount: theCase.chequeAmount != null ? String(theCase.chequeAmount) : "",
        });
        setEditResponsibleIds(theCase.responsibleUserIds ?? []);
        setStageOther(!!theCase.legalStage && !LEGAL_STAGES.includes(theCase.legalStage));
        setEditing(true);
        setError(null);
    }
    async function saveEdit() {
        setEditSaving(true);
        setError(null);
        try {
            await api.put(`/cases/${id}`, {
                caseNumber: editForm.caseNumber.trim() || undefined,
                courtNumber: editForm.courtNumber.trim() || undefined,
                requestRefNumber: editForm.requestRefNumber.trim() || undefined,
                fileNumber: editForm.fileNumber.trim() || undefined,
                authorizedSignatory: editForm.authorizedSignatory.trim() || undefined,
                legalStage: editForm.legalStage.trim() || undefined,
                caseStatus: DIRECTLY_SETTABLE_STATUSES.includes(editForm.caseStatus) && editForm.caseStatus !== theCase?.caseStatus ? editForm.caseStatus : undefined,
                priority: editForm.priority || undefined,
                legalFirmId: editForm.legalFirmId || undefined,
                chequeAmount: editForm.chequeAmount ? Number(editForm.chequeAmount) : undefined,
                responsibleUserIds: user?.role === "MANAGEMENT" && editResponsibleIds.length ? editResponsibleIds : undefined,
            });
            setEditing(false);
            loadCase();
        }
        catch {
            setError("Couldn't save changes.");
        }
        finally {
            setEditSaving(false);
        }
    }
    if (error)
        return <div style={{ color: "var(--st-risk)" }}>{error}</div>;
    if (!theCase)
        return <div style={{ color: "var(--ink-faint)" }}>Loading…</div>;
    return (<div>
      <Link to="/legal-cases" style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: "0.82rem", fontWeight: 600, color: "var(--accent-ink)", textDecoration: "none", marginBottom: 12 }}>
        ← Back to Dashboard
      </Link>
      <div style={{ background: "var(--surface)", border: "1px solid var(--line-soft)", borderRadius: 10, padding: "20px 22px", marginBottom: 18 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <h1 style={{ fontFamily: "var(--font-display)", fontWeight: 600, fontSize: "1.5rem", margin: 0 }}>
            {theCase.caseCode}
          </h1>
          <span className="mono" style={{ background: "var(--st-pending-bg)", color: "var(--st-pending)", fontSize: "0.68rem", fontWeight: 600, padding: "3px 9px", borderRadius: 5 }}>
            {theCase.priority}
          </span>
          <span className="mono" style={{ background: "var(--st-info-bg)", color: "var(--st-info)", fontSize: "0.68rem", fontWeight: 600, padding: "3px 9px", borderRadius: 5 }}>
            {theCase.legalStage.replace(/_/g, " ")}
          </span>
        </div>
        <div style={{ fontSize: "0.85rem", color: "var(--ink-soft)", marginTop: 5 }}>{theCase.caseType}</div>

        <div className="lg-pair" style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 16, marginTop: 18, paddingTop: 16, borderTop: "1px solid var(--line-soft)" }}>
          <Stat label="Receivable" value={formatQAR(theCase.financialSummary.totalRecoverable)}/>
          <Stat label="Recovered" value={formatQAR(theCase.financialSummary.amountReceived)} color="var(--st-open)"/>
          <Stat label="Outstanding" value={formatQAR(theCase.financialSummary.outstandingBalance)} color="var(--st-risk)"/>
          <Stat label="Recovery %" value={`${theCase.financialSummary.recoveryPercentage.toFixed(2)}%`}/>
          <Stat label="Cheque Amount" value={theCase.chequeAmount != null ? formatQAR(theCase.chequeAmount) : "—"}/>
        </div>
      </div>

      <div style={{ display: "flex", gap: 2, borderBottom: "1px solid var(--line)", marginBottom: 18 }}>
        {TABS.map((t) => (<div key={t} onClick={() => setTab(t)} style={{
                padding: "10px 16px",
                fontSize: "0.85rem",
                fontWeight: tab === t ? 700 : 500,
                color: tab === t ? "var(--ink)" : "var(--ink-faint)",
                borderBottom: tab === t ? "2px solid var(--accent)" : "2px solid transparent",
                cursor: "pointer",
            }}>
            {t}
          </div>))}
      </div>

      {tab === "Overview" && !editing && (<Panel>
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
            <button onClick={startEdit} style={linkBtn}>Edit</button>
          </div>
          <div className="lg-stack" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "20px 28px" }}>
            <Field label="Case Number" value={theCase.caseNumber || "—"}/>
            <Field label="Court Number" value={theCase.courtNumber || "—"}/>
            <Field label="Request Ref No." value={theCase.requestRefNumber || "—"}/>
            <Field label="File No." value={theCase.fileNumber || "—"}/>
            <Field label="Case Date" value={theCase.caseDate ? new Date(theCase.caseDate).toLocaleDateString() : "—"}/>
            <Field label="Legal Stage" value={theCase.legalStage.replace(/_/g, " ")}/>
            <Field label="Case Status" value={theCase.caseStatus.replace(/_/g, " ")}/>
            <Field label="Priority" value={theCase.priority}/>
            <Field label="Customer" value={customers.find((c) => c.id === theCase.customerId)?.name || "—"}/>
            <Field label="Company" value={companies.find((c) => c.id === theCase.companyId)?.name || "—"}/>
            <Field label="Legal Firm" value={legalFirms.find((f) => f.id === theCase.legalFirmId)?.name || "—"}/>
            <Field label="Authorized Signatory" value={theCase.authorizedSignatory || "—"}/>
            <Field label="Responsible Person(s)" value={theCase.responsibleUserIds.map((rid) => users.find((u) => u.id === rid)?.name ?? "—").join(", ") || "—"}/>
          </div>
          {theCase.remarks && (<div style={{ marginTop: 20, paddingTop: 18, borderTop: "1px solid var(--line-soft)" }}>
              <div className="mono" style={{ fontSize: "0.66rem", color: "var(--ink-faint)", textTransform: "uppercase", marginBottom: 6 }}>
                Remarks
              </div>
              <div style={{ fontSize: "0.88rem", color: "var(--ink-soft)" }}>{theCase.remarks}</div>
            </div>)}
        </Panel>)}

      {tab === "Overview" && editing && (<Panel>
          <div className="lg-stack" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "16px 28px" }}>
            <EditField label="Case Number">
              <input value={editForm.caseNumber} onChange={(e) => setEditForm((f) => ({ ...f, caseNumber: e.target.value }))} style={inputStyle}/>
            </EditField>
            <EditField label="Court Number">
              <input value={editForm.courtNumber} onChange={(e) => setEditForm((f) => ({ ...f, courtNumber: e.target.value }))} style={inputStyle}/>
            </EditField>
            <EditField label="Request Ref No.">
              <input value={editForm.requestRefNumber} onChange={(e) => setEditForm((f) => ({ ...f, requestRefNumber: e.target.value }))} style={inputStyle}/>
            </EditField>
            <EditField label="File No.">
              <input value={editForm.fileNumber} onChange={(e) => setEditForm((f) => ({ ...f, fileNumber: e.target.value }))} style={inputStyle}/>
            </EditField>
            <EditField label="Case Status">
              {DIRECTLY_SETTABLE_STATUSES.includes(theCase.caseStatus) ? (<select value={editForm.caseStatus} onChange={(e) => setEditForm((f) => ({ ...f, caseStatus: e.target.value }))} style={inputStyle}>
                  {DIRECTLY_SETTABLE_STATUSES.map((s) => (<option key={s} value={s}>
                      {s === "NEW_CASE" ? "New Case" : s.replace(/_/g, " ")}
                    </option>))}
                </select>) : (<div style={{ fontSize: "0.82rem", color: "var(--ink-soft)", padding: "9px 0" }}>
                  {theCase.caseStatus.replace(/_/g, " ")} — change this from the Approvals tab.
                </div>)}
            </EditField>
            <EditField label="Legal Stage">
              <select value={stageOther ? "__OTHER__" : editForm.legalStage} onChange={(e) => {
                if (e.target.value === "__OTHER__") {
                    setStageOther(true);
                    setEditForm((f) => ({ ...f, legalStage: LEGAL_STAGES.includes(f.legalStage) ? "" : f.legalStage }));
                }
                else {
                    setStageOther(false);
                    setEditForm((f) => ({ ...f, legalStage: e.target.value }));
                }
            }} style={inputStyle}>
                <option value="">— Select —</option>
                {LEGAL_STAGES.map((s) => (<option key={s} value={s}>
                    {s === "NEW_CASE" ? "New Case" : s}
                  </option>))}
                <option value="__OTHER__">Other…</option>
              </select>
              {stageOther && (<input placeholder="Type the stage" value={editForm.legalStage} onChange={(e) => setEditForm((f) => ({ ...f, legalStage: e.target.value }))} style={{ ...inputStyle, marginTop: 6 }}/>)}
            </EditField>
            <EditField label="Priority">
              <select value={editForm.priority} onChange={(e) => setEditForm((f) => ({ ...f, priority: e.target.value }))} style={inputStyle}>
                {["LOW", "MEDIUM", "HIGH", "CRITICAL"].map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </EditField>
            <EditField label="Legal Firm">
              <select value={editForm.legalFirmId} onChange={(e) => setEditForm((f) => ({ ...f, legalFirmId: e.target.value }))} style={inputStyle}>
                <option value="">Not assigned</option>
                {legalFirms.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select>
            </EditField>
            <EditField label="Authorized Signatory">
              <input value={editForm.authorizedSignatory} onChange={(e) => setEditForm((f) => ({ ...f, authorizedSignatory: e.target.value }))} style={inputStyle}/>
            </EditField>
            <EditField label="Cheque Amount (QAR)">
              <input type="number" min="0" step="0.01" value={editForm.chequeAmount} onChange={(e) => setEditForm((f) => ({ ...f, chequeAmount: e.target.value }))} style={inputStyle}/>
            </EditField>
            {user?.role === "MANAGEMENT" && (<EditField label="Responsible Person(s)">
                <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 14px", border: "1px solid var(--line)", borderRadius: 8, padding: "9px 10px" }}>
                  {coordinators.map((u) => (<label key={u.id} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: "0.82rem", cursor: "pointer" }}>
                      <input type="checkbox" checked={editResponsibleIds.includes(u.id)} onChange={(e) => setEditResponsibleIds((ids) => (e.target.checked ? [...ids, u.id] : ids.filter((rid) => rid !== u.id)))}/>
                      {u.name}
                    </label>))}
                </div>
              </EditField>)}
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 20 }}>
            <button onClick={saveEdit} disabled={editSaving} style={btnStyle}>{editSaving ? "Saving…" : "Save"}</button>
            <button onClick={() => setEditing(false)} style={linkBtn}>Cancel</button>
          </div>
        </Panel>)}

      {tab === "Timeline" && (<Panel>
          <div style={{ marginBottom: 18 }}>
            <textarea placeholder="Add an update…" value={newComment} onChange={(e) => setNewComment(e.target.value)} rows={2} style={{ width: "100%", boxSizing: "border-box", border: "1px solid var(--line)", borderRadius: 8, padding: "9px 12px", fontSize: "0.85rem", fontFamily: "inherit", resize: "vertical" }}/>
            <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 8, flexWrap: "wrap" }}>
              <label style={{ fontSize: "0.78rem", color: "var(--ink-soft)" }}>
                Date{" "}
                <input type="date" value={updateDate} max={new Date().toISOString().slice(0, 10)} onChange={(e) => setUpdateDate(e.target.value)} style={{ border: "1px solid var(--line)", borderRadius: 8, padding: "6px 8px", fontSize: "0.8rem", fontFamily: "inherit" }}/>
              </label>
              <input key={updateFileKey} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" onChange={(e) => setUpdateFile(e.target.files?.[0] ?? null)} style={{ fontSize: "0.78rem", flex: 1, minWidth: 160 }} title="Attachment (optional)"/>
              <button onClick={addUpdate} disabled={addingUpdate || !newComment} style={{ background: "var(--brand)", color: "#fff", fontWeight: 600, fontSize: "0.82rem", padding: "9px 16px", borderRadius: 8, border: "none", cursor: "pointer" }}>
                {addingUpdate ? "Adding…" : "Add update"}
              </button>
            </div>
          </div>

          {timeline.length === 0 && <div style={{ color: "var(--ink-faint)", fontSize: "0.85rem" }}>No updates recorded yet.</div>}
          {timeline.map((e) => (<div key={e.id} style={{ display: "flex", gap: 14, paddingBottom: 18, marginBottom: 18, borderBottom: "1px solid var(--line-soft)" }}>
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: "var(--accent)", marginTop: 6, flex: "0 0 auto" }}/>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <div style={{ fontWeight: 600, fontSize: "0.86rem" }}>{users.find((u) => u.id === e.updatedBy)?.name ?? "—"} <span className="mono" style={{ fontWeight: 400, fontSize: "0.68rem", color: "var(--ink-faint)" }}>{(e.userRole || "").replace(/_/g, " ")}</span></div>
                  <div className="mono" style={{ fontSize: "0.72rem", color: "var(--ink-faint)" }}>
                    {new Date(e.occurredAt).toLocaleString()}
                  </div>
                </div>
                {editingUpdateId === e.id ? (<div style={{ marginTop: 6 }}>
                    <textarea value={editComment} onChange={(ev) => setEditComment(ev.target.value)} rows={3} style={{ width: "100%", boxSizing: "border-box", border: "1px solid var(--line)", borderRadius: 8, padding: "8px 10px", fontSize: "0.85rem", fontFamily: "inherit", resize: "vertical" }}/>
                    <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 6 }}>
                      <input type="date" value={editDate} max={new Date().toISOString().slice(0, 10)} onChange={(ev) => setEditDate(ev.target.value)} style={{ border: "1px solid var(--line)", borderRadius: 8, padding: "5px 8px", fontSize: "0.8rem", fontFamily: "inherit" }}/>
                      <button onClick={() => saveEditUpdate(e)} disabled={savingEdit || !editComment.trim()} style={{ background: "var(--brand)", color: "#fff", fontWeight: 600, fontSize: "0.78rem", padding: "6px 14px", borderRadius: 8, border: "none", cursor: "pointer" }}>
                        {savingEdit ? "Saving…" : "Save"}
                      </button>
                      <button onClick={() => setEditingUpdateId(null)} style={{ background: "transparent", border: "1px solid var(--line)", color: "var(--ink-soft)", fontWeight: 600, fontSize: "0.78rem", padding: "6px 12px", borderRadius: 8, cursor: "pointer" }}>
                        Cancel
                      </button>
                    </div>
                  </div>) : (<div style={{ fontSize: "0.86rem", color: "var(--ink-soft)", marginTop: 5 }}>
                    {e.comment}
                    {e.editedAt && (<span className="mono" title={`Edited ${new Date(e.editedAt).toLocaleString()}`} style={{ fontSize: "0.7rem", color: "var(--ink-faint)", marginLeft: 8 }}>
                        (edited)
                      </span>)}
                    {(user?.role === "MANAGEMENT" || user?.id === e.updatedBy) && (<button onClick={() => startEditUpdate(e)} style={{ background: "transparent", border: "none", color: "var(--accent-ink)", fontWeight: 600, fontSize: "0.76rem", marginLeft: 10, cursor: "pointer" }}>
                        Edit
                      </button>)}
                  </div>)}
                {e.attachments?.map((a) => (<button key={a.id} onClick={() => downloadDocument(theCase.id, a.id, a.fileName)} style={{ background: "transparent", border: "none", color: "var(--accent-ink)", fontWeight: 600, fontSize: "0.78rem", padding: "6px 10px 0 0", cursor: "pointer" }}>
                    📎 {a.fileName}
                  </button>))}
              </div>
            </div>))}
        </Panel>)}

      {tab === "Financials" && (<Panel>
          <FinancialsPanel theCase={theCase} onCaseUpdated={setCase}/>
          <ChequesPanel caseId={theCase.id} canRequestReturn={theCase.caseStatus !== "CLOSED"}/>
          <ExpensePanel theCase={theCase} onCaseUpdated={setCase}/>
        </Panel>)}

      {tab === "Documents" && (<Panel>
          <DocumentsPanel caseId={theCase.id}/>
        </Panel>)}

      {tab === "Follow-ups" && user && (<Panel>
          <FollowUpsPanel caseId={theCase.id} currentUserId={user.id}/>
        </Panel>)}

      {tab === "Approvals" && (<Panel>
          <ApprovalsPanel caseId={theCase.id} onCaseUpdated={loadCase}/>
        </Panel>)}

      {tab === "Audit History" && (<Panel>
          <AuditHistoryPanel caseId={theCase.id}/>
        </Panel>)}
    </div>);
}
function Stat({ label, value, color }) {
    return (<div>
      <div className="mono" style={{ fontSize: "0.66rem", color: "var(--ink-faint)", textTransform: "uppercase" }}>
        {label}
      </div>
      <div className="mono tabnum" style={{ fontSize: "1.05rem", fontWeight: 600, marginTop: 3, color: color ?? "var(--ink)" }}>
        {value}
      </div>
    </div>);
}
function Field({ label, value }) {
    return (<div>
      <div className="mono" style={{ fontSize: "0.66rem", color: "var(--ink-faint)", textTransform: "uppercase" }}>
        {label}
      </div>
      <div style={{ fontSize: "0.88rem", marginTop: 4 }}>{value}</div>
    </div>);
}
function EditField({ label, children }) {
    return (<div>
      <label className="mono" style={{ display: "block", fontSize: "0.66rem", color: "var(--ink-faint)", textTransform: "uppercase", marginBottom: 5 }}>
        {label}
      </label>
      {children}
    </div>);
}
function Panel({ children }) {
    return (<div style={{ background: "var(--surface)", border: "1px solid var(--line-soft)", borderRadius: 10, padding: "20px 22px" }}>
      {children}
    </div>);
}
const inputStyle = { border: "1px solid var(--line)", borderRadius: 8, padding: "8px 10px", fontSize: "0.82rem", fontFamily: "inherit", width: "100%" };
const btnStyle = { background: "var(--brand)", color: "#fff", fontWeight: 600, fontSize: "0.82rem", padding: "9px 16px", borderRadius: 8, border: "none", cursor: "pointer" };
const linkBtn = { border: "1px solid var(--line)", background: "var(--surface)", color: "var(--ink-soft)", fontWeight: 600, fontSize: "0.75rem", padding: "6px 12px", borderRadius: 7, cursor: "pointer" };
